<script setup>
// [xleaders] XO-09 — kênh Zalo cá nhân rớt là chuyện xảy ra đều (hết hạn cookie, sale logout Zalo
// Web từ điện thoại). Trước đây Chatwoot không hiện gì: tin không đi mà không ai biết vì sao.
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import ZaloPersonalReconnect from 'dashboard/components/widgets/ZaloPersonalReconnect.vue';
import ZaloPersonalChannel from 'dashboard/api/channel/zaloPersonalChannel';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

const props = defineProps({
  inbox: {
    type: Object,
    default: () => ({}),
  },
});

// Không có kênh realtime nào đẩy thay đổi inbox xuống dashboard (`INBOX_UPDATED` chỉ chảy vào
// WebhookListener), nên phải poll. 30s là đủ cho yêu cầu "biết trong vòng vài phút".
const POLL_INTERVAL_MS = 30000;
// Hai trạng thái bắt buộc quét QR lại: `disconnected` là bị Zalo kick (code 3003, cố ý không tự
// nối lại), `qr_required` là vòng backoff của sidecar đã thử hết mà vẫn không được.
const TERMINAL_STATUSES = ['disconnected', 'qr_required'];

const status = ref(null);
// Màn QR mở ngay tại đây chứ không đẩy sang trang cài đặt inbox: route đó là `administrator`-only
// (luật upstream, dùng chung cho mọi kênh), mà ticket chốt ai mở được inbox thì quét lại được —
// sale thường là `agent` nên bấm sang đó chỉ bị router đá về trang mặc định, không một lời nào.
const showRescanModal = ref(false);
let pollHandle = null;

const isZaloPersonal = computed(
  () => props.inbox?.channel_type === INBOX_TYPES.ZALO_PERSONAL
);
const needsRescan = computed(
  () => isZaloPersonal.value && TERMINAL_STATUSES.includes(status.value)
);
const isReconnecting = computed(
  () => isZaloPersonal.value && status.value === 'reconnecting'
);

const fetchStatus = async () => {
  // Chốt lại inbox của lượt hỏi này: sale chuyển hội thoại nhanh hơn một round-trip là chuyện
  // thường, và một response về muộn của inbox Zalo vừa rời đi sẽ sơn dải cảnh báo đỏ lên hội
  // thoại Facebook đang mở, kèm nút trỏ sang inbox sai.
  const requestedInboxId = props.inbox.id;
  try {
    const { data } =
      await ZaloPersonalChannel.connectionStatus(requestedInboxId);
    if (requestedInboxId !== props.inbox.id) return;
    status.value = data.status;
  } catch (error) {
    // Giữ nguyên thứ đang hiện: một lỗi mạng nhất thời không phải bằng chứng kênh đã rớt.
  }
};

const stopPolling = () => {
  if (pollHandle) clearInterval(pollHandle);
  pollHandle = null;
};

watch(
  () => (isZaloPersonal.value ? props.inbox.id : null),
  inboxId => {
    stopPolling();
    status.value = null;
    // Đổi inbox thì màn QR đang mở không còn thuộc về ai — đóng lại thay vì để nó âm thầm quay
    // sang hỏi trạng thái của inbox mới.
    showRescanModal.value = false;
    if (!inboxId) return;
    fetchStatus();
    pollHandle = setInterval(fetchStatus, POLL_INTERVAL_MS);
  },
  { immediate: true }
);

onBeforeUnmount(stopPolling);

// [xleaders] XO-10: thông báo "Zalo rớt kết nối" (chuông/push) mở hội thoại này kèm `?zalo_rescan=1`
// — cú bấm vào thông báo đã là ý muốn quét lại, nên bật thẳng hộp thoại QR thay vì bắt bấm thêm
// lần nữa trên dải cảnh báo. Chỉ bật khi kênh thật sự cần quét (poll đầu tiên trả terminal): thông
// báo cũ bấm muộn sau khi kênh đã nối lại thì không mở gì cả.
const route = useRoute();
watch([needsRescan, () => route.query.zalo_rescan], ([needed, flagged]) => {
  if (needed && flagged) showRescanModal.value = true;
});

const onReconnected = () => {
  status.value = 'connected';
};
</script>

<template>
  <div>
    <Banner
      v-if="needsRescan"
      color="ruby"
      class="mx-2 mt-2"
      :action-label="$t('CONVERSATION.ZALO_PERSONAL_DISCONNECTED.ACTION')"
      @action="showRescanModal = true"
    >
      {{ $t('CONVERSATION.ZALO_PERSONAL_DISCONNECTED.MESSAGE') }}
    </Banner>
    <Banner v-else-if="isReconnecting" color="amber" class="mx-2 mt-2">
      {{ $t('CONVERSATION.ZALO_PERSONAL_RECONNECTING.MESSAGE') }}
    </Banner>
    <woot-modal v-if="isZaloPersonal" v-model:show="showRescanModal">
      <woot-modal-header
        :header-title="$t('INBOX_MGMT.ZALO_PERSONAL_RECONNECT.MODAL_TITLE')"
      />
      <div class="p-8 pt-0">
        <ZaloPersonalReconnect
          :inbox="inbox"
          auto-start
          @reconnected="onReconnected"
        />
      </div>
    </woot-modal>
  </div>
</template>
